import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FiSearch, FiCheckCircle } from "react-icons/fi";
import { searchPostcode } from "../services/daumPostcode";
import "./AddressSearch.css";

/**
 * Korean address entry.
 *
 * The postcode and road address are read-only on purpose — they can only be set
 * by picking from the official 도로명주소 database, so they cannot be mistyped.
 * The customer types only the detail line (동/호수), which no database can know.
 *
 * `value`  : the address object being edited (our API shape)
 * `onChange`: called with the patch to merge in
 */
const AddressSearch = ({ value = {}, onChange, error, detailError }) => {
  const { t } = useTranslation();
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    setSearching(true);
    try {
      const picked = await searchPostcode();
      if (picked) {
        onChange({ ...picked, country: "South Korea" });
      }
    } catch {
      toast.error(t("address.searchFailed"));
    } finally {
      setSearching(false);
    }
  };

  const line = value.road_address || value.address_line || value.street || "";
  const hasAddress = Boolean(value.postal_code && line);

  return (
    <div className="adr-wrap">
      <label className="adr-label">
        {t("address.postcode")} <span className="adr-req">*</span>
      </label>

      <div className="adr-row">
        <input
          className={`adr-input adr-zip ${error ? "adr-error" : ""}`}
          type="text"
          value={value.postal_code || ""}
          placeholder={t("address.postcodePlaceholder")}
          readOnly
          onClick={handleSearch}
        />
        <button
          type="button"
          className="adr-btn"
          onClick={handleSearch}
          disabled={searching}
        >
          <FiSearch />
          {searching ? t("address.searching") : t("address.search")}
        </button>
      </div>

      <input
        className={`adr-input ${error ? "adr-error" : ""}`}
        type="text"
        value={line}
        placeholder={t("address.roadPlaceholder")}
        readOnly
        onClick={handleSearch}
      />

      <input
        className={`adr-input ${detailError ? "adr-error" : ""}`}
        type="text"
        value={value.address_details || ""}
        placeholder={t("address.detailPlaceholder")}
        onChange={(e) => onChange({ address_details: e.target.value })}
        disabled={!hasAddress}
      />

      {error && <span className="adr-msg">{error}</span>}
      {detailError && <span className="adr-msg">{detailError}</span>}

      {hasAddress && !error && (
        <span className="adr-ok">
          <FiCheckCircle /> {t("address.verified")}
        </span>
      )}
      {!hasAddress && !error && (
        <span className="adr-hint">{t("address.hint")}</span>
      )}
    </div>
  );
};

export default AddressSearch;
